import React from 'react';
import { Navigate } from 'react-router-dom';

import TenantLogin from '../pages/Authentication/TenantLogin';
import Logout from '../pages/Authentication/Logout';
import Register from '../pages/Authentication/Register';
import ForgetPwd from '../pages/Authentication/ForgetPassword';
import SaaSDashboard from '../pages/Dashboard/index.tsx';
import TenantDashboard from '../pages/Dashboard/TenantDashboard/index.js';
import AddMedicineWizardForm from '../pages/Medicine/AddNew/AddMedicine';
import PurchaseInvoiceForm from '../pages/Purchases/Invoices/PurchaseInvoiceForm';
import InvoiceDetail from '../pages/Tenant/Purchase/PurchaseInvoiceDetail';
import MedicineList from '../pages/Medicine/MedicineList';
import MedicineDetail from '../pages/Medicine/Detail';
import SalesList from '../pages/Sales/SalesList';
import MedicineSearchTabs from '../pages/Medicine/Search/MedicineSearchTabs';
import InvoiceConfirmation from '../pages/Purchases/Invoices/InvoiceConfirmation';
import SaaSLandingPage from 'pages/SaaS/Landing/SaaSLandingPage';

import { getSubdomain, isSaaSSubdomain } from '../utils/getSubdomain';
import SaaSLogin from 'pages/Auth/SaaSLogin';
import TwoFactorVerification from 'pages/Auth/TwoFactorVerification';
import ResetPassword from 'pages/Auth/ResetPassword';
import TenantRequestList from 'pages/SaaS/AppTenants/TenantRequestList';
import TenantsList from 'pages/SaaS/AppTenants/TenantList';
import TenantShopsList from 'pages/Tenant/Shops/ShopList';
import ShopDetailPage from 'pages/Tenant/Shops/EditShopPage';
import ShopLayout from 'layouts/authorized/ShopLayout';
import ShopFormPage from 'pages/Tenant/Shops/ShopFormPage';
import ShopItemFormPage from 'pages/Tenant/Items/ShopItemFormPage';
import ManufacturerFormPage from 'pages/Tenant/Manufacturers/ManufacturerFormPage';
import ManufacturerList from 'pages/Tenant/Manufacturers/ManufacturerList';
import BusinessSettingsPage from 'pages/Tenant/BusinessSettings/BusinessSettingsPage';
import UserListPage from 'pages/Tenant/Users/UserListPage';
import RequirePermission from 'components/Common/RequirePermission';
import { PERMISSIONS } from 'constants/permissions';
import UserFormPage from 'pages/Tenant/Users/UserFormPage';
import ChangeStaffPasswordPage from 'pages/Tenant/Users/ChangeStaffPasswordPage';
import PermissionsPage from 'pages/Tenant/Users/PermissionsPage';
import { SHOP_PATHS, MANUFACTURER_PATHS, USER_PATHS } from './routesConstants';
import MedicineItemList from 'pages/Tenant/Items/List/MedicineItemsList';
import PurchaseEntryPage from 'pages/Tenant/Purchase/MedicinePurchasePage';
import ShopWrapper from 'context/providers/ShopWrapper';
import ItemUnitList from 'pages/Tenant/ItemUnits/ItemUnitList';
import AddUpdateItemUnitForm from 'components/Form/Tenant/ItemUnit/AddUpdateItemUnitForm';
import ItemUnitFormPage from 'pages/Tenant/ItemUnits/ItemUnitFormPage';
import PurchaseInvoiceList from 'pages/Tenant/Purchase/PurchaseInvoiceList';
import InventoryList from 'pages/Tenant/Inventory/InventoryList';
import InventoryEntry from 'pages/Tenant/Inventory/InventoryEntry';
import MedicineSellEntryPage from 'pages/Tenant/SellInvoice/MedicineSellEntryPage';
import SellInvoiceList from 'pages/Tenant/SellInvoice/SellInvoiceList';
import SellDashboardPage from 'pages/Tenant/SellInvoice/SellDashboardPage';
import CatalogItemsList from 'pages/SaaS/Catalog/CatalogItemsList';
import SharedCategoriesList from 'pages/SaaS/Catalog/SharedCategoriesList';
import ItemUnitsList from 'pages/SaaS/ItemUnits/ItemUnitsList';
import SellInvoiceDetailPage from 'pages/Tenant/SellInvoice/SellInvoiceDetailPage';
import SellAdjustmentItemsList from 'pages/Tenant/Adjustments/SellAdjustmentItemsList';
import PurchaseAdjustmentItemsList from 'pages/Tenant/Adjustments/PurchaseAdjustmentItemsList';
import LedgerEntriesList from 'pages/Tenant/Ledger/LedgerEntriesList';
import StockMovementList from 'pages/Tenant/StockMovement/StockMovementList';

const subdomain = getSubdomain();

const dashboardComponent = isSaaSSubdomain() ? <SaaSDashboard /> : <TenantShopsList />;
const loginComponent = isSaaSSubdomain() ? <SaaSLogin /> : <TenantLogin />;

const protectedShopRoutes = [
  { path: SHOP_PATHS.EDIT, component: <ShopDetailPage />, layout: 'SHOP' },
  { path: SHOP_PATHS.ADD_ITEM, component: <ShopItemFormPage />, layout: 'SHOP' },
  { path: SHOP_PATHS.EDIT_ITEM, component: <ShopItemFormPage />, layout: 'SHOP' },
  { path: SHOP_PATHS.ITEMS, component: <MedicineItemList />, layout: 'SHOP' },

  { path: SHOP_PATHS.PURCHASE_ENTRY, component: <PurchaseEntryPage />, layout: 'SHOP' },
  { path: SHOP_PATHS.PURCHASE_INVOICES, component: <PurchaseInvoiceList />, layout: 'SHOP' },
  { path: SHOP_PATHS.EDIT_PURCHASE_INVOICE, component: <PurchaseEntryPage />, layout: 'SHOP' },
  { path: SHOP_PATHS.PURCHASE_INVOICE_DETAIL, component: <InvoiceDetail />, layout: 'SHOP' },
  {
    path: SHOP_PATHS.PURCHASE_ADJUSTMENT_ITEMS,
    component: <PurchaseAdjustmentItemsList />,
    layout: 'SHOP',
  },

  // sell entry form
  { path: SHOP_PATHS.MEDICINE_SELL_ENTRY, component: <MedicineSellEntryPage />, layout: 'SHOP' },
  { path: SHOP_PATHS.MEDICINE_SELL_INVOICES, component: <SellInvoiceList />, layout: 'SHOP' },
  {
    path: SHOP_PATHS.MEDICINE_SELL_INVOICES_EDIT,
    component: <MedicineSellEntryPage />,
    layout: 'SHOP',
  },
  {
    path: SHOP_PATHS.MEDICINE_SELL_DB_VIEW,
    component: <SellDashboardPage />,
    layout: 'SHOP',
  },

  {
    path: SHOP_PATHS.MEDICINE_SELL_INVOICE_DETAIL,
    component: <SellInvoiceDetailPage />,
    layout: 'SHOP',
  },
  {
    path: SHOP_PATHS.SELL_ADJUSTMENT_ITEMS,
    component: <SellAdjustmentItemsList />,
    layout: 'SHOP',
  },

  { path: SHOP_PATHS.INVENTORIES, component: <InventoryList />, layout: 'SHOP' },
  { path: SHOP_PATHS.ADD_INVENTORY, component: <InventoryEntry />, layout: 'SHOP' },
  { path: SHOP_PATHS.EDIT_INVENTORY, component: <InventoryEntry />, layout: 'SHOP' },

  { path: SHOP_PATHS.ITEM_UNITS, component: <ItemUnitList />, layout: 'SHOP' },
  { path: SHOP_PATHS.ADD_UNIT, component: <ItemUnitFormPage />, layout: 'SHOP' },
  { path: SHOP_PATHS.EDIT_UNIT, component: <ItemUnitFormPage />, layout: 'SHOP' },

  { path: SHOP_PATHS.LEDGER_ENTRIES, component: <LedgerEntriesList />, layout: 'SHOP' },
  { path: SHOP_PATHS.STOCK_MOVEMENTS, component: <StockMovementList />, layout: 'SHOP' },
];

// Wrap components with ShopWrapper dynamically
const wrappedProtectedShopRoutes = protectedShopRoutes.map(({ path, component, layout }) => ({
  path,
  layout,
  component: <ShopWrapper>{component}</ShopWrapper>,
}));

const allRoutes = {
  public: [
    { path: '/login', component: loginComponent },
    { path: '/two-factor-verification', component: <TwoFactorVerification /> },
    { path: '/logout', component: <Logout /> },
    { path: '/forgot-password', component: <ForgetPwd /> },
    { path: '/reset-password', component: <ResetPassword /> },
    { path: '/register', component: <Register /> },
  ],
  protected: [
    { path: '/', exact: true, component: dashboardComponent },
    { path: '/dashboard', component: dashboardComponent },
    { path: '/medicines', component: <MedicineList /> },
    { path: '/shops', component: <TenantShopsList /> },
    { path: '/shops/add', component: <ShopFormPage /> },
    { path: MANUFACTURER_PATHS.BASE, component: <ManufacturerList /> },
    { path: MANUFACTURER_PATHS.ADD_MANUFACTURER, component: <ManufacturerFormPage /> },
    { path: MANUFACTURER_PATHS.EDIT_MANUFACTURER, component: <ManufacturerFormPage /> },
    {
      path: USER_PATHS.BASE,
      exact: true,
      component: (
        <RequirePermission permission={PERMISSIONS.VIEW_STAFF}>
          <UserListPage />
        </RequirePermission>
      ),
    },
    {
      path: USER_PATHS.ADD_USER,
      component: (
        <RequirePermission permission={PERMISSIONS.CREATE_STAFF}>
          <UserFormPage />
        </RequirePermission>
      ),
    },
    {
      path: USER_PATHS.EDIT_USER,
      component: (
        <RequirePermission permission={PERMISSIONS.EDIT_STAFF}>
          <UserFormPage />
        </RequirePermission>
      ),
    },
    {
      path: USER_PATHS.CHANGE_PASSWORD,
      component: (
        <RequirePermission permission={PERMISSIONS.RESET_PASSWORD}>
          <ChangeStaffPasswordPage />
        </RequirePermission>
      ),
    },
    {
      path: USER_PATHS.PERMISSIONS,
      component: (
        <RequirePermission permission={PERMISSIONS.VIEW_PERMISSIONS}>
          <PermissionsPage />
        </RequirePermission>
      ),
    },
    { path: SHOP_PATHS.BUSINESS_SETTINGS, component: <BusinessSettingsPage /> },
    { path: '/medicines/add', component: <AddMedicineWizardForm /> },
    { path: '/medicines/:medicineId', component: <MedicineDetail /> },
    { path: '/purchases/invoices', component: <PurchaseInvoiceList /> },
    { path: '/purchases/invoices/add', component: <PurchaseInvoiceForm /> },
    { path: '/purchases/invoices/confirmation', component: <InvoiceConfirmation /> },
    { path: '/sales/invoices', component: <SalesList /> },
    { path: '/sales/invoices/add', component: <MedicineSearchTabs /> },
  ],
  saasProtected: [
    { path: '/', exact: true, component: dashboardComponent },
    { path: '/dashboard', component: dashboardComponent },
    { path: '/saas/tenants', component: <TenantsList /> },
    { path: '/saas/tenant-requests', component: <TenantRequestList /> },
    { path: '/saas/catalog-items', component: <CatalogItemsList /> },
    { path: '/saas/shared-categories', component: <SharedCategoriesList /> },
    { path: '/saas/item-units', component: <ItemUnitsList /> },
  ],
  landing: [
    {
      path: '/',
      exact: true,
      component: subdomain ? <Navigate to="/dashboard" /> : <SaaSLandingPage />,
    },
  ],
  fallback: subdomain
    ? [{ path: '*', component: <Navigate to="/dashboard" /> }]
    : [{ path: '*', component: <Navigate to="/" /> }],
};

const publicRoutes = subdomain
  ? [...allRoutes.public, ...allRoutes.landing, ...allRoutes.fallback]
  : [...allRoutes.public, ...allRoutes.landing, ...allRoutes.fallback];

// const publicRoutes = subdomain
//   ? [...allRoutes.public, ...allRoutes.landing, ...allRoutes.fallback]
//   : [...allRoutes.landing, ...allRoutes.fallback];

// Combine protected routes and protectedShopRoutes into one array
const authProtectedRoutes = isSaaSSubdomain()
  ? [...allRoutes.saasProtected, ...wrappedProtectedShopRoutes]
  : [...allRoutes.protected, ...wrappedProtectedShopRoutes];

export { authProtectedRoutes, publicRoutes };
